'use client';

export type ConsentRegion = 'gdpr' | 'ccpa' | 'none';

export interface ConsentRecord {
  version: '1.0';
  timestamp: string;
  region: ConsentRegion;
  choices: {
    necessary: true;
    analytics: boolean;
    marketing: boolean;
    preferences: boolean;
  };
  userAgent: string;
}

const CONSENT_KEY = 'providerprofile_consent_v1';

export function detectRegion(): ConsentRegion {
  if (typeof navigator === 'undefined') return 'gdpr';

  const lang = navigator.language || '';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

  const euTZPrefixes = [
    'Europe/',
    'Atlantic/Azores',
    'Atlantic/Canary',
    'Atlantic/Faroe',
    'Atlantic/Madeira',
    'Arctic/Longyearbyen',
  ];

  const usTZPrefixes = ['America/'];

  const euCountryCodes = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI',
    'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
    'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK', 'GB', 'IS', 'LI',
    'NO', 'CH', 'NG',
  ];

  const isEU =
    euTZPrefixes.some((p) => tz.startsWith(p)) ||
    euCountryCodes.some((c) => lang.toUpperCase().endsWith('-' + c));

  const isUS =
    usTZPrefixes.some((p) => tz.startsWith(p)) &&
    (lang.startsWith('en-US') || lang.startsWith('en-CA'));

  if (isEU) return 'gdpr';
  if (isUS) return 'ccpa';
  return 'none';
}

export function saveConsent(record: ConsentRecord): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
  } catch (e) {
    console.error('Failed to save consent:', e);
  }
}

export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.error('Failed to retrieve consent:', e);
    return null;
  }
}

export function createConsentRecord(
  region: ConsentRegion,
  choices: Partial<ConsentRecord['choices']> = {}
): ConsentRecord {
  return {
    version: '1.0',
    timestamp: new Date().toISOString(),
    region,
    choices: {
      necessary: true,
      analytics: choices.analytics ?? false,
      marketing: choices.marketing ?? false,
      preferences: choices.preferences ?? false,
    },
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

export function logConsentAction(
  action: string,
  record: ConsentRecord
): void {
  if (typeof window === 'undefined') return;
  try {
    const logs = JSON.parse(localStorage.getItem('providerprofile_consent_log') || '[]');
    logs.push({
      action,
      timestamp: new Date().toISOString(),
      record,
    });
    if (logs.length > 50) logs.shift();
    localStorage.setItem('providerprofile_consent_log', JSON.stringify(logs));
  } catch (e) {
    console.error('Failed to log consent action:', e);
  }
}
